import db from "@/database";
import { getSyncData, getSyncMeta } from "@/services/external/sync-service";
import dayjs from "dayjs";
import {
  getLastConfig,
  getFilteredItems,
} from "@/services/local/config-service";
import { downloadConsentFormFromAws } from "@/services/external/aws-service";
import { CONSENT_FILE_ID } from "@/services/local/settings-service";

const state = {
  dialog: false,
};

const mutations = {
  changeDialog(state, value) {
    state.dialog = value;
  },
  forceCloseDialog(state) {
    state.dialog = false;
  },
};

const actions = {
  async download({ commit, dispatch }) {
    commit("changeDialog", true);
    return new Promise(async (resolve, reject) => {
      const therapys = await db.therapy.toArray();
      const { data: result } = await getSyncData();
      const data = result.data;

      const storedImages = await db.images.toArray();
      const storedSettings = await db.settings.toArray();

      await db.delete();
      await db.open();

      db.images.bulkAdd(storedImages);
      db.settings.bulkAdd(storedSettings);

      await Promise.all([
        db.category.bulkAdd(data.categories),
        db.type.bulkAdd(data.types),
        db.symptom.bulkAdd(data.symptoms),
        db.pathogen.bulkAdd(data.pathogens),
        db.sequence.bulkAdd(data.sequences),
        db.filteredItems.add({ data: data.filteredItems }),
        db.par.bulkAdd(data.pares),
        db.point.bulkAdd(data.points),
        db.pointsOrder.bulkAdd(data.pointsOrder),
        db.maritalStatus.bulkAdd(data.maritalStatus),
        db.tumoralPhenomenon.bulkAdd(data.tumoralPhenomena),
        db.resolutionZone.bulkAdd(data.resolutionZones),
        db.vascularPhenomenon.bulkAdd(data.vascularPhenomena),
        db.potentialSpace.bulkAdd(data.potentialSpaces),
        db.flowType.bulkAdd(data.flowTypes),
        db.oligoelementsType.bulkAdd(data.oligoelementsTypes),
        db.chakra.bulkAdd(data.chakras),
        db.chromosome.bulkAdd(data.chromosomes),
        db.therapyTemplates.bulkAdd(data.therapyTemplates),
        db.complementaryTherapy.bulkAdd(data.complementaryTherapies),
        dispatch("checkAndDownloadConsentTermFile", data.consentTermUrl),
        dispatch("downloadImages", data.points),

        db.config.add({
          lastUpdate: result.loadedAt,
          loadHash: result.hash,
        }),
        db.therapy.bulkAdd(therapys),
      ])
        .catch((error) => {
          console.error(error);
          reject(error);
        })
        .finally(() => {
          commit("changeDialog", false);
        });
      resolve();
    });
  },
  async verifyIfNeedsUpdate({ dispatch }) {
    if (!navigator.onLine) return false;

    try {
      const config = await getLastConfig();

      if (!config || !config.lastUpdate) {
        return true;
      }

      const { data } = await getSyncMeta();

      const currentFilteredItems = await getFilteredItems();

      if (currentFilteredItems.length === 0) {
        return true;
      }

      const currentTherapyTemplates =
        currentFilteredItems[0]?.data?.therapyTemplates;

      const serverTherapyTemplates = data.therapyTemplates;

      currentTherapyTemplates?.sort((a, b) => a - b);
      serverTherapyTemplates?.sort((a, b) => a - b);
      const therapyTemplatesChanged =
        JSON.stringify(currentTherapyTemplates) !==
        JSON.stringify(serverTherapyTemplates);

      if (data.hash !== config.loadHash || therapyTemplatesChanged) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log("Erro ao verificar necessidade de atualização:", error);
      return true;
    }
  },
  async verifyIfNeedsUpdateAndDownload({ dispatch }) {
    if (await dispatch("verifyIfNeedsUpdate")) {
      dispatch("download");
    }
  },
  async checkAndDownloadConsentTermFile({}, consentTermUrl) {
    if (consentTermUrl) {
      const hasConsentTermFile = await db.settings
        .where("consentTermUrl")
        .equals(consentTermUrl)
        .count();

      if (!hasConsentTermFile) {
        const consentTermFile = await downloadConsentFormFromAws(
          consentTermUrl
        );
        db.settings.add({
          id: CONSENT_FILE_ID,
          consentTermUrl: consentTermUrl || "",
          consentTerm: consentTermFile,
        });
      }
    }
  },
  async downloadImages({}, points) {
    const imageUrls = points
      .map((point) => point.image)
      .filter((url) => url && url.startsWith("https"));

    const storedImages = await db.images.toArray();

    // URLs das imagens que precisam ser baixadas (estão em points mas não no banco)
    const imageUrlsToDownload = imageUrls.filter((url) => {
      return !storedImages.some((img) => img.url === url);
    });

    // URLs das imagens que estão no banco mas não estão mais em points (devem ser removidas)
    const imageUrlsToRemove = storedImages
      .filter((img) => !imageUrls.includes(img.url))
      .map((img) => img.url);

    // Remove imagens obsoletas do banco
    if (imageUrlsToRemove.length > 0) {
      await db.images.where("url").anyOf(imageUrlsToRemove).delete();
    }

    // Função auxiliar para tentar baixar até 3 vezes
    async function tryDownload(url, retries = 3) {
      let lastError;
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const image = await downloadConsentFormFromAws(url);
          return image;
        } catch (error) {
          lastError = error;
          console.warn(
            `Falha ao baixar ${url} (tentativa ${attempt} de ${retries})`
          );
          if (attempt === retries) {
            console.error(`Falha definitiva ao baixar ${url}:`, error);
          }
        }
      }
      return null;
    }

    // Faz o download das imagens de forma concorrente, com tentativas
    const downloadedImages = await Promise.all(
      imageUrlsToDownload.map(async (imageUrl) => {
        const image = await tryDownload(imageUrl, 3);
        if (image) {
          return {
            url: imageUrl,
            blob: image,
          };
        }
        return null;
      })
    );

    // Filtra downloads que falharam
    const validImages = downloadedImages.filter((img) => img !== null);

    await db.images.bulkAdd(validImages);
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
